/**
 * The form that finally stores a credential VALUE from the browser.
 *
 * Until Plan C fase 2.2 there was no such form anywhere in this product,
 * and there could not be: a credential lived in a deploy-time
 * environment variable, so every screen could point at one and none
 * could set one. This is that missing half, and it is driven entirely by
 * the server catalog — the field list, its labels, its help text and
 * which boxes are masked all arrive from
 * `builder/integrations_catalog.py`. Nothing about a channel is decided
 * here, so adding a channel never means editing this file.
 *
 * TWO RULES IT ENFORCES IN THE MARKUP
 * -----------------------------------
 * 1. **It never pre-fills.** A stored value is not readable, by design
 *    (`tenancy.secret_store` returns metadata and has no verb that
 *    returns a value), so there is nothing to pre-fill WITH. Every
 *    submit is a full REPLACE. Pretending otherwise — showing dots that
 *    stand for a value we do not have — would be a UI that lies about
 *    what it knows.
 * 2. **`autoComplete="off"` and `type="password"` on every secret box**,
 *    so a browser password manager does not offer to remember a
 *    customer's SMTP password in the operator's profile.
 *
 * The submitted object is exactly the blob the consuming parser reads:
 * one `value` for a raw credential, or the named keys for a document.
 * That shape comes from the catalog too, for the concrete reason that
 * getting it wrong would only surface at the first real send.
 */

import { Button, Input } from "@omnitok/ui";
import { useState } from "react";

import { useT } from "@/i18n";
import type { CatalogField } from "@/lib/integrations-api";

export interface SecretValueFormProps {
  fields: CatalogField[];
  /** True while the PUT is in flight. */
  saving: boolean;
  onSubmit: (values: Record<string, string>) => void;
  onCancel: () => void;
  /** Shown above the fields when a value is already stored. */
  replacing: boolean;
}

export function SecretValueForm({
  fields,
  saving,
  onSubmit,
  onCancel,
  replacing,
}: SecretValueFormProps): JSX.Element {
  const { t } = useT();
  const [values, setValues] = useState<Record<string, string>>({});

  const missing = fields
    .filter((f) => f.required && !(values[f.key] ?? "").trim())
    .map((f) => f.key);

  return (
    <form
      className="mt-3 space-y-3 rounded border border-neutral-300 bg-white p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (missing.length > 0) return;
        onSubmit(values);
      }}
    >
      <p className="text-xs text-neutral-600">
        {replacing
          ? t("integrations.secret.replaceNotice")
          : t("integrations.secret.firstNotice")}
      </p>

      {fields.map((field) => (
        <label key={field.key} className="block text-xs">
          <span className="font-medium text-neutral-800">
            {field.label}
            {field.required ? " *" : ""}
          </span>
          <Input
            className="mt-1"
            // A masked box for anything the catalog marks secret. The
            // number/url/email types are only there for the keyboard and
            // the browser's own shape hint — the backend re-validates.
            type={field.secret ? "password" : field.type}
            // Never let a password manager capture a CUSTOMER's
            // credential into the OPERATOR's browser profile.
            autoComplete="off"
            data-testid={`secret-field-${field.key}`}
            placeholder={field.placeholder}
            value={values[field.key] ?? ""}
            onChange={(e) =>
              setValues({ ...values, [field.key]: e.target.value })
            }
          />
          <span className="mt-1 block text-xs leading-snug text-neutral-500">
            {field.help}
          </span>
        </label>
      ))}

      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={saving || missing.length > 0}>
          {saving
            ? t("integrations.secret.saving")
            : t("integrations.secret.saveBtn")}
        </Button>
        <Button
          size="sm"
          type="button"
          variant="outline"
          disabled={saving}
          onClick={onCancel}
        >
          {t("integrations.cancelBtn")}
        </Button>
      </div>
    </form>
  );
}

export default SecretValueForm;
